import styles from './create-focus-account-dialog.module.scss';
import { Col, Row } from 'reactstrap';
import { useContext, useState } from 'react';
import { v4 } from 'uuid';
import { GetCurrentCompany } from '../../customer-context/customer-context';
import { CheckboxLabel, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, OrderStateEnum, PrimaryButton, SnackBarContext, TextField } from '@frontend-workspace/react-shared-lib';
import { useTranslation } from 'react-i18next';
import { useMakeRequestHook } from '../../requestUtils';

/* eslint-disable-next-line */
export interface CreateFocusAccountDialogProps {
  open: boolean,
  onClose: () => void,
}

export function CreateFocusAccountDialog(props: CreateFocusAccountDialogProps) {
  const company = GetCurrentCompany();
  const makeRequest = useMakeRequestHook();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [isStudent, setIsStudent] = useState<boolean>(false);
  const [sendEmail, setSendEmail] = useState<boolean>(true);
  const [emailError, setEmailError] = useState('');

  const handleIsStudent = (event: React.ChangeEvent<Element>, checked: boolean) => setIsStudent(checked);
  const handleSendEmail = (event: React.ChangeEvent<Element>, checked: boolean) => setSendEmail(checked);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setEmail(input);

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(input)) {
      setEmailError(t('standard.pleaseEnterValidEmailAddress') || '');
    } else {
      setEmailError('');
    }
  };

  const handleCreateFocusAccount = () => {
    const newFocusAccount = {
      activationDate: new Date(),
      companyName: company?.name,
      companyNumber: company?.number,
      state: OrderStateEnum.Open,
      emailAddress: email,
      firstName: firstName,
      lastName: lastName,
      isAcademic: isStudent,
      id: v4(),
      isWebkuaOrder: true,
      orgNumber: company?.orgNr
    }
    makeRequest(`accounts/`, 'POST', newFocusAccount).then(result => {
      setSnackbarMessage({ content: t('ceateFocusAccount.createAccount'), type: "success" });
      props.onClose();
    }).catch(() => setSnackbarMessage({ content: t('standard.couldNotChangeFocusAccount'), type: "error" }));
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t('ceateFocusAccount.createFocusAccount')}
      </DialogTitle>
      <DialogContent>
        <div className="d-flex flex-column mb-3">
          <Row>
            <Col md={8}>
              <div className='mb-3'>
                <TextField
                  value={firstName || ''}
                  onChange={(e) => setFirstName(e.target.value)}
                  fullWidth
                  label={t('standard.firstName')}
                  name='firstName'
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={8}>
              <div className='mb-3'>
                <TextField
                  value={lastName || ''}
                  onChange={(e) => setLastName(e.target.value)}
                  fullWidth
                  label={t('standard.lastName')}
                  name='lastName'
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className='mb-3'>
                <TextField
                  value={email || ''}
                  onChange={handleEmailChange}
                  fullWidth
                  label={t('standard.email')}
                  name='email'
                  error={!!emailError}
                  helperText={emailError}
                />
              </div>
            </Col>
          </Row>
          <div className='d-inline-flex mb-1'>
            <CheckboxLabel
              label={t('standard.student')}
              checked={isStudent}
              onChange={handleIsStudent}
            />
          </div>
          <div className='d-inline-flex mb-1'>
            <CheckboxLabel
              label={t('standard.sendEmail')}
              checked={sendEmail}
              onChange={handleSendEmail}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleCreateFocusAccount}
          className='minButtonWidth'>
          {t('standard.add')}
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default CreateFocusAccountDialog;